// AnalyseButton — der Einstieg in die Risikoanalyse als EIN Knopf.
//
// Vorher war das eine große Kachel im Reiter „Risiken", zusammen mit den 14
// Detailanalyse-Kacheln. Fachlich gehört der Fragebogen aber nicht in die
// Risikobetrachtung, sondern zu den Stammdaten, die er befüllt — deshalb steht
// er jetzt im Reiter „Allgemein" über Branche und Tätigkeit.
//
// Die Zustandslogik ist unverändert die aus `useAnalysenState`: derselbe
// 3-Zustands-Automat (nicht ausgefüllt / Kundenmodus / Beratermodus) bestimmt
// die Beschriftung, dasselbe `RisikoanalyseStartPopup` öffnet sich.

import * as React from "react";
import { useCallback, useState } from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import RisikoanalyseStartPopup from "./RisikoanalyseStartPopup";
import { Icons } from "./riskIcons";
import { useAnalysenState } from "./useAnalysenState";

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    rowGap: "8px",
    flexWrap: "wrap",
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ...shorthands.padding("10px", "16px"),
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    fontWeight: 700,
    cursor: "pointer",
    transitionProperty: "background-color, transform",
    transitionDuration: "130ms",
    ":hover": { backgroundColor: REFINED.aub700, transform: "translateY(-1px)" },
  },
  // row-reverse: Anfang ist rechts — der Knopf steht außen, der Status links davon.
  rowRechts: { flexDirection: "row-reverse", justifyContent: "flex-start" },
  ico: { width: "16px", height: "16px", display: "inline-flex" },
  status: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.84rem",
    fontWeight: 600,
    color: REFINED.ink2,
  },
  statusOn: { color: REFINED.aub },
});

type Props = {
  contactId: number | undefined;
  /** Nach dem Schließen des Dialogs zusätzlich aufrufen (z. B. Kontakt neu laden). */
  onRefresh?: () => void;
  /** Rechtsbündig, der Knopf ganz außen (Fuß der Firmenakte, User 02.11.2026). */
  rechts?: boolean;
};

export const AnalyseButton: React.FC<Props> = ({ contactId, onRefresh, rechts }) => {
  const styles = useStyles();
  const [offen, setOffen] = useState(false);

  const {
    popupMode,
    submittedToken,
    existingKundenLink,
    generalAusgefuellt,
    reload,
  } = useAnalysenState(contactId);

  const reloadAlles = useCallback(() => {
    reload();
    onRefresh?.();
  }, [reload, onRefresh]);

  const aktionLabel =
    popupMode === "beratermodus"
      ? "Auswertung ansehen"
      : popupMode === "kundenmodus"
      ? "Auswertung & Beratermodus"
      : "Risikoanalyse starten";

  const statusLabel =
    popupMode === "beratermodus"
      ? "Ausgefüllt"
      : popupMode === "kundenmodus"
      ? "Kundenmodus ausgefüllt"
      : "Noch nicht ausgefüllt";

  return (
    <div className={mergeClasses(styles.row, rechts && styles.rowRechts)}>
      <button type="button" className={styles.btn} onClick={() => setOffen(true)}>
        <span className={styles.ico}>
          <Icons.clipboard />
        </span>
        Analyse
      </button>
      <span className={mergeClasses(styles.status, generalAusgefuellt && styles.statusOn)}>
        <span style={{ width: 13, height: 13, display: "inline-flex" }}>
          {generalAusgefuellt ? <Icons.checkSimple /> : <Icons.clock />}
        </span>
        {statusLabel} — {aktionLabel}
      </span>

      <RisikoanalyseStartPopup
        open={offen}
        onClose={() => {
          setOffen(false);
          reloadAlles();
        }}
        contactId={contactId}
        mode={popupMode}
        submittedToken={submittedToken}
        existingKundenLink={existingKundenLink}
        onRefresh={reloadAlles}
      />
    </div>
  );
};

export default AnalyseButton;
